import { useEffect, useId, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { clampPoint, displayPoint, modelPoint, snapPoint, type BoardState, type Point, type Tool } from '../lib/board'
import { along, pathLength } from '../lib/play'
import { courtDimensions, courtViewport, drawingViewport } from '../lib/courtGeometry'
import CourtMarkings from './CourtMarkings'
import type { AnimationView, WhiteboardAnimator } from '../lib/whiteboardAnimation'
import { fitWhiteboard, whiteboardDimensions, whiteboardViewport } from '../lib/whiteboardLayout'
import { themeColors } from '../lib/theme'

type Gesture =
  | { type: 'player'; id: string; start: Point; moved: boolean }
  | { type: 'stroke'; id: string }
  | { type: 'erase' }
  | { type: 'endpoint'; id: string; index: number }
  | { type: 'catch'; id: string }
  | { type: 'animate' }
  | null

type Props = {
  board: BoardState
  tool: Tool
  onGestureStart: () => void
  onGestureEnd: () => void
  onChange: (updater: (board: BoardState) => BoardState) => void
  onSelectPlayer: (id: string) => void
  onPlayerTap?: (id: string) => void
  selectedPlayerId: string | null
  selectedPlayerIds?: string[]
  onTarget?: (playerId: string | undefined, point: Point) => void
  onBallTap?: () => void
  readOnly?: boolean
  catchMarkers?: { id: string; point: Point }[]
  onCatchEdit?: (id: string, point: Point) => void
  animation?: AnimationView & { animator: WhiteboardAnimator }
  compact?: boolean
}

const WIDTH = 760

function toSvgPoint(event: ReactPointerEvent<SVGSVGElement>, height: number, fullCourt = false, bounds = courtViewport(fullCourt), compact = false): Point {
  const svg = event.currentTarget
  const clamp = (p: Point) => compact ? {
    x: Math.max((bounds.x+30)/WIDTH, Math.min((bounds.x+bounds.width-30)/WIDTH,p.x)),
    y: Math.max((bounds.y+30)/height, Math.min((bounds.y+bounds.height-30)/height,p.y)),
  } : clampPoint(p,fullCourt,bounds)
  const matrix = svg.getScreenCTM()
  if (matrix) {
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse())
    return clamp({ x: point.x / WIDTH, y: point.y / height })
  }
  const rect = svg.getBoundingClientRect()
  const [x, y, width, viewHeight] = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number)
  return clamp({
    x: (x + (event.clientX - rect.left) / rect.width * width) / WIDTH,
    y: (y + (event.clientY - rect.top) / rect.height * viewHeight) / height,
  })
}

function pointsToPath(points: Point[], flipped: boolean, height: number, dribble = false): string {
  if (dribble && points.length > 1) {
    const samples = Math.max(2, Math.ceil(pathLength(points)*70))
    points = Array.from({ length: samples+1 },(_,i) => {
      const p = along(points,i/samples), next = along(points,Math.min(1,(i+1)/samples)), previous = along(points,Math.max(0,(i-1)/samples))
      const dx = next.x-previous.x, dy = next.y-previous.y, length = Math.hypot(dx,dy) || 1
      const amplitude = i === 0 || i === samples ? 0 : (i%2 ? .006 : -.006)
      return { x: p.x-dy/length*amplitude, y: p.y+dx/length*amplitude }
    })
  }
  return points.map((point, index) => {
    const shown = displayPoint(point, flipped)
    return `${index ? 'L' : 'M'} ${shown.x * WIDTH} ${shown.y * height}`
  }).join(' ')
}

function nearbyStroke(board: BoardState, point: Point): string | undefined {
  let closest: { id: string; distance: number } | undefined
  for (const stroke of board.strokes) {
    for (let index = 0; index < stroke.points.length; index++) {
      const sample = stroke.points[index]
      const next = stroke.points[index + 1] || sample
      const dx = next.x - sample.x, dy = next.y - sample.y
      const length = dx * dx + dy * dy
      const t = length ? Math.max(0, Math.min(1, ((point.x - sample.x) * dx + (point.y - sample.y) * dy) / length)) : 0
      const distance = Math.hypot(sample.x + t * dx - point.x, sample.y + t * dy - point.y)
      if (distance < 0.035 && (!closest || distance < closest.distance)) closest = { id: stroke.id, distance }
    }
  }
  return closest?.id
}

export default function Court({ board, tool, onGestureStart, onGestureEnd, onChange, onSelectPlayer, onPlayerTap, selectedPlayerId, selectedPlayerIds, onTarget, onBallTap, readOnly, catchMarkers = [], onCatchEdit, animation, compact = false }: Props) {
  const { height: HEIGHT } = compact ? whiteboardDimensions(board.fullCourt) : courtDimensions(!!board.fullCourt)
  const initialFit = compact ? whiteboardViewport(board) : drawingViewport(board)
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const gesture = useRef<Gesture>(null)
  const activePointer = useRef<number | null>(null)
  const beforeGesture = useRef<BoardState | null>(null)
  const [selectedStroke, setSelectedStroke] = useState<string | null>(null)
  const [viewport, setViewport] = useState(initialFit)
  // Fit once per document/layout change. Dragging must not make the court recede.
  useEffect(() => { setViewport(compact ? whiteboardViewport(board) : drawingViewport(board)) }, [HEIGHT, board.id, compact])
  const fit = compact ? viewport : initialFit
  const svgRef = useRef<SVGSVGElement>(null)
  const [marker, setMarker] = useState({ x: 1, y: 1, hit: 23 })
  useEffect(() => {
    if (!compact || !svgRef.current || typeof ResizeObserver === 'undefined') return
    const svg = svgRef.current, frame = svg.parentElement!, stage = frame.parentElement!
    const resize = () => {
      const style = getComputedStyle(stage)
      const width = stage.clientWidth-parseFloat(style.paddingLeft)-parseFloat(style.paddingRight)
      const height = stage.clientHeight-parseFloat(style.paddingTop)-parseFloat(style.paddingBottom)
      if (width <= 0 || height <= 0) return
      const size = fitWhiteboard(width,height,viewport)
      frame.style.width = `${size.width}px`; frame.style.height = `${size.height}px`
      const sx = size.width/viewport.width, sy = size.height/viewport.height
      const scale = Math.min(1,22/(20*sy))
      setMarker({ x: scale*sy/sx, y: scale, hit: Math.max(23,22/(sy*scale)) })
    }
    const observer = new ResizeObserver(resize)
    observer.observe(stage)
    resize()
    return () => { observer.disconnect(); frame.style.removeProperty('width'); frame.style.removeProperty('height') }
  }, [compact, viewport.x, viewport.y, viewport.width, viewport.height])
  const contacts = useRef(new Map<number, Point>())
  const penPointer = useRef<number | null>(null)
  const [dragCatch, setDragCatch] = useState<{ id: string; point: Point } | null>(null)
  const catchPoint = useRef<Point | null>(null)
  const pinch = useRef<{ distance: number; center: Point; view: typeof viewport } | null>(null)

  const cancelCurrentGesture = () => {
    if (gesture.current?.type === 'animate') animation?.animator.drop(undefined,true)
    else if (beforeGesture.current) { const before = beforeGesture.current; onChange(() => before); onGestureEnd() }
    beforeGesture.current = null
  }

  const zoom = (factor: number) => setViewport(view => {
    const width = Math.max(fit.width / 3, Math.min(fit.width, view.width * factor))
    const height = width * fit.height / fit.width
    return { x: view.x + (view.width - width) / 2, y: view.y + (view.height - height) / 2, width, height }
  })

  const eraseAt = (point: Point) => {
    onChange((current) => {
      const id = nearbyStroke(current, point)
      return id ? { ...current, strokes: current.strokes.filter((stroke) => stroke.id !== id), updatedAt: Date.now() } : current
    })
  }

  const handlePointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (readOnly) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    if (event.pointerType === 'touch' && penPointer.current !== null) return
    const hit = event.target as SVGElement
    if (onTarget) { const player = hit.closest('[data-player-id],[data-player-hit]'); onTarget(player?.getAttribute('data-player-id') || player?.getAttribute('data-player-hit') || undefined, modelPoint(toSvgPoint(event, HEIGHT, board.fullCourt, fit, compact), board.flipped)); return }
    if (onBallTap && hit.closest('[data-ball]')) { onBallTap(); return }
    if (event.pointerType === 'pen') {
      if (activePointer.current !== null || pinch.current) {
        cancelCurrentGesture()
        contacts.current.clear(); activePointer.current = null; gesture.current = null; pinch.current = null
      }
      penPointer.current = event.pointerId
    }
    contacts.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (event.pointerType === 'touch' && contacts.current.size === 2) {
      cancelCurrentGesture()
      gesture.current = null
      activePointer.current = null
      const [a, b] = [...contacts.current.values()]
      pinch.current = { distance: Math.hypot(a.x-b.x, a.y-b.y), center: { x: (a.x+b.x)/2, y: (a.y+b.y)/2 }, view: viewport }
      event.currentTarget.setPointerCapture(event.pointerId)
      return
    }
    if (activePointer.current !== null || pinch.current) return
    activePointer.current = event.pointerId
    beforeGesture.current = structuredClone(board)
    event.currentTarget.setPointerCapture(event.pointerId)
    const target = event.target as SVGElement
    const playerHit = target.closest('[data-player-id],[data-player-hit]')
    const playerId = playerHit?.getAttribute('data-player-id') || playerHit?.getAttribute('data-player-hit')
    const display = toSvgPoint(event, HEIGHT, board.fullCourt, fit, compact)
    const point = modelPoint(display, board.flipped)
    if (tool === 'animate' && animation) {
      beforeGesture.current = null
      setSelectedStroke(null)
      if (target.closest('[data-ball]') || playerId) {
        gesture.current = { type: 'animate' }
        animation.animator.begin(target.closest('[data-ball]') ? { kind: 'ball' } : { kind: 'player', id: playerId! },point)
      } else gesture.current = null
      return
    }
    onGestureStart()

    const catchId = target.closest('[data-catch-id]')?.getAttribute('data-catch-id')
    if (catchId && onCatchEdit) { gesture.current = { type: 'catch', id: catchId }; catchPoint.current = point; return }

    const endpoint = target.closest('[data-endpoint]')
    if (endpoint && tool === 'select' && selectedStroke) {
      gesture.current = { type: 'endpoint', id: selectedStroke, index: Number(endpoint.getAttribute('data-endpoint')) }
      return
    }
    if (tool === 'select' && !playerId) {
      setSelectedStroke(nearbyStroke(board, point) || null)
    } else setSelectedStroke(null)

    if (playerId && tool !== 'eraser') {
      gesture.current = { type: 'player', id: playerId, start: point, moved: false }
      if (!onPlayerTap) onSelectPlayer(playerId)
      return
    }

    if (tool === 'eraser') {
      gesture.current = { type: 'erase' }
      eraseAt(point)
      return
    }

    if (tool === 'marker' || tool === 'line') {
      const id = crypto.randomUUID()
      gesture.current = { type: 'stroke', id }
      onChange((current) => ({
        ...current,
        strokes: [...current.strokes, { id, points: [tool === 'line' ? snapPoint(point, current) : point], kind: tool === 'line' ? 'arrow' : 'ink', color: themeColors.midnight }],
        updatedAt: Date.now(),
      }))
      return
    }

    gesture.current = null
    onSelectPlayer('')
  }

  const handlePointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (contacts.current.has(event.pointerId)) contacts.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (pinch.current) {
      if (compact) return
      const [a, b] = [...contacts.current.values()]
      if (!a || !b) return
      const start = pinch.current
      const scale = start.distance / Math.max(1, Math.hypot(a.x-b.x,a.y-b.y))
      const width = Math.max(fit.width/3, Math.min(fit.width, start.view.width * scale))
      const height = width * fit.height / fit.width
      const rect = event.currentTarget.getBoundingClientRect()
      const dx = ((a.x+b.x)/2 - start.center.x) * start.view.width / rect.width
      const dy = ((a.y+b.y)/2 - start.center.y) * start.view.height / rect.height
      setViewport({ x: start.view.x + (start.view.width-width)/2-dx, y: start.view.y+(start.view.height-height)/2-dy, width, height })
      return
    }
    if (!gesture.current || activePointer.current !== event.pointerId) return
    const point = modelPoint(toSvgPoint(event, HEIGHT, board.fullCourt, fit, compact), board.flipped)
    if (gesture.current.type === 'animate') {
      animation?.animator.move(point)
    } else if (gesture.current.type === 'player') {
      if (Math.hypot(point.x - gesture.current.start.x, point.y - gesture.current.start.y) < .012) return
      gesture.current.moved = true
      const id = gesture.current.id
      onChange((current) => ({
        ...current,
        players: current.players.map((player) => player.id === id ? { ...player, ...point } : player),
        updatedAt: Date.now(),
      }))
    } else if (gesture.current.type === 'catch') {
      catchPoint.current = point; setDragCatch({ id: gesture.current.id, point })
    } else if (gesture.current.type === 'endpoint') {
      const { id, index } = gesture.current
      onChange(current => ({ ...current, strokes: current.strokes.map(stroke => stroke.id === id ? { ...stroke, points: stroke.points.map((p, i) => i === index ? snapPoint(point, current) : p) } : stroke) }))
    } else if (gesture.current.type === 'stroke') {
      const { id } = gesture.current
      onChange((current) => ({
        ...current,
        strokes: current.strokes.map((stroke) => {
          if (stroke.id !== id) return stroke
          return stroke.kind === 'arrow'
            ? { ...stroke, points: [stroke.points[0], snapPoint(point, current)] }
            : { ...stroke, points: [...stroke.points, point] }
        }),
        updatedAt: Date.now(),
      }))
    } else {
      eraseAt(point)
    }
  }

  const finishGesture = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (penPointer.current === event.pointerId) penPointer.current = null
    contacts.current.delete(event.pointerId)
    if (pinch.current) { if (contacts.current.size === 0) pinch.current = null; return }
    if (activePointer.current !== event.pointerId) return
    const canceled = event.type !== 'pointerup'
    const animated = gesture.current?.type === 'animate'
    if (animated) animation?.animator.drop(modelPoint(toSvgPoint(event,HEIGHT,board.fullCourt,fit,compact),board.flipped),canceled)
    if (canceled && beforeGesture.current) {
      const before = beforeGesture.current
      onChange(() => before)
    }
    if (!canceled && gesture.current?.type === 'catch' && catchPoint.current) onCatchEdit?.(gesture.current.id, catchPoint.current)
    if (!canceled && gesture.current?.type === 'player' && !gesture.current.moved) onPlayerTap?.(gesture.current.id)
    catchPoint.current = null; setDragCatch(null)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    gesture.current = null
    activePointer.current = null
    beforeGesture.current = null
    if (!animated) onGestureEnd()
  }

  const visiblePlayers = board.players.filter((player) => player.side === 'offense' || board.showDefense)

  return (
    <>
    {!compact && <div className="court-view-controls" aria-label="Court view"><button onClick={() => zoom(.8)} aria-label="Zoom in">+</button><button onClick={() => zoom(1.25)} aria-label="Zoom out">−</button><button onClick={() => setViewport(fit)}>Fit court</button></div>}
    {tool === 'select' && selectedStroke && board.strokes.some(s => s.id === selectedStroke && s.kind === 'arrow') && <div className="stroke-controls"><label>Line style<select value={board.strokes.find(s => s.id === selectedStroke)?.style || 'cut'} onChange={event => { const style = event.target.value as 'cut' | 'pass' | 'screen'; onGestureStart(); onChange(current => ({ ...current, strokes: current.strokes.map(s => s.id === selectedStroke ? { ...s, style } : s) })); onGestureEnd() }}><option value="cut">Cut</option><option value="pass">Pass</option><option value="screen">Screen</option></select></label></div>}
    <svg
      ref={svgRef}
      className={`court tool-${tool}`}
      preserveAspectRatio={compact ? 'none' : undefined}
      viewBox={`${viewport.x} ${viewport.y} ${viewport.width} ${viewport.height}`}
      role="application"
      aria-label="Basketball whiteboard"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishGesture}
      onPointerCancel={finishGesture}
      onLostPointerCapture={finishGesture}
    >
      <defs>
        <clipPath id={`${uid}-ball-hit`}><rect x="-10" y="-150" width="300" height="300" /></clipPath>
        <pattern id={`${uid}-wood`} width="320" height="96" patternUnits="userSpaceOnUse">
          <rect width="320" height="96" fill={themeColors.courtMaple} />
          <path d="M0 0H320V24H0Z" fill={themeColors.courtMapleLight} />
          <path d="M0 48H320V72H0Z" fill="#E9C17E" />
          <path d="M0 72H320V96H0Z" fill={themeColors.courtMapleDark} />
          <path d="M0 24H320M0 48H320M0 72H320M0 96H320M86 0V24M240 24V48M140 48V72M287 72V96" fill="none" stroke="#9f753e" strokeWidth=".7" opacity=".2" />
          <path d="M0 8C70 4 110 13 180 8S270 4 320 8M0 17C90 12 155 22 235 17S290 15 320 17M0 32C60 37 130 28 210 32S290 36 320 32M0 42C100 45 165 37 245 42S300 44 320 42M0 56C60 52 150 61 220 56S285 53 320 56M0 66C90 62 135 70 230 66S290 62 320 66M0 80C70 84 155 76 230 80S285 84 320 80M0 90C85 87 170 94 240 90S290 88 320 90" fill="none" stroke="#aa7b40" strokeWidth=".6" opacity=".14" />
        </pattern>
        <marker id={`${uid}-arrowhead`} viewBox="0 0 10 10" refX="8" refY="5" markerUnits="userSpaceOnUse" markerWidth="24" markerHeight="24" orient="auto-start-reverse">
          <path d="M0 0 10 5 0 10z" fill={themeColors.midnight} />
        </marker>
        <marker id={`${uid}-screen-end`} viewBox="0 0 10 10" refX="5" refY="5" markerUnits="userSpaceOnUse" markerWidth="24" markerHeight="24" orient="auto"><path d="M5 0v10" stroke={themeColors.midnight} strokeWidth="2" /></marker>
        <filter id={`${uid}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="5" stdDeviation="5" floodOpacity=".2" />
        </filter>
      </defs>

      <CourtMarkings fullCourt={board.fullCourt} flipped={board.flipped} floor={`url(#${uid}-wood)`} apron={fit} compact={compact} />

      {[...board.strokes,...animation?.previews || []].map((stroke, index) => (
        <path
          key={stroke.id}
          d={pointsToPath(stroke.points, board.flipped, HEIGHT, stroke.style === 'dribble')}
          fill="none"
          stroke={stroke.color}
          strokeWidth={stroke.kind === 'arrow' ? 9 : 7}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={stroke.style === 'pass' ? '16 12' : undefined}
          markerEnd={stroke.kind === 'arrow' ? `url(#${uid}-${stroke.style === 'screen' ? 'screen-end' : 'arrowhead'})` : undefined}
          className={index < board.strokes.length ? 'annotation-stroke' : 'animation-route'}
          pointerEvents={index < board.strokes.length ? undefined : 'none'}
        />
      ))}

      {animation?.destinations.map((p,i) => { const shown = displayPoint(p,board.flipped); return <g key={`destination-${i}`} transform={`translate(${shown.x*WIDTH} ${shown.y*HEIGHT})${compact ? ` scale(${marker.x} ${marker.y})` : ''}`}><circle className="animation-destination" r={compact ? 24 : 30} fill={themeColors.electricBlue} fillOpacity=".12" stroke={themeColors.electricBlueDark} strokeWidth="3" strokeDasharray="6 5" pointerEvents="none" /></g> })}

      {board.strokes.filter(s => tool === 'select' && s.id === selectedStroke && s.kind === 'arrow').flatMap(s => s.points.map((p, index) => {
        const shown = displayPoint(p, board.flipped)
        return <circle key={index} data-endpoint={index} cx={shown.x * WIDTH} cy={shown.y * HEIGHT} r="18" fill="white" stroke={themeColors.electricBlue} strokeWidth="5" />
      }))}

      {/* Put generous hit areas behind every visible marker so a nearby player's
          invisible target cannot intercept a tap on another player's body. */}
      {compact && visiblePlayers.map(player => { const p = displayPoint(player,board.flipped); return <circle key={`hit-${player.id}`} data-player-hit={player.id} transform={`translate(${p.x*WIDTH} ${p.y*HEIGHT}) scale(${marker.x} ${marker.y})`} r={marker.hit} fill="transparent" /> })}
      {visiblePlayers.map((player) => {
        const shown = displayPoint(player, board.flipped)
        const x = shown.x * WIDTH
        const y = shown.y * HEIGHT
        const selected = player.id === selectedPlayerId || !!selectedPlayerIds?.includes(player.id)
        return (
          <g key={player.id} data-player-id={player.id} transform={`translate(${x} ${y})${compact ? ` scale(${marker.x} ${marker.y})` : ''}`} className="player-marker" filter={`url(#${uid}-shadow)`}>
            {(animation?.targets.includes(player.id) || animation?.eligible.includes(player.id)) && <circle className="animation-target" r={compact ? 29 : 40} fill="none" stroke={animation.targets.includes(player.id) ? themeColors.success : themeColors.electricBlueDark} strokeWidth="3" strokeDasharray={animation.targets.includes(player.id) ? undefined : '5 6'} pointerEvents="none" />}
            {selected && <circle r={compact ? 26 : 38} fill="none" stroke="#fff" strokeWidth="3" strokeDasharray="7 5" />}
            <circle data-player-body="true" r={compact ? 20 : 30} fill={player.side === 'offense' ? themeColors.electricBlue : themeColors.danger} stroke="#fff" strokeWidth={compact ? 3 : 5} />
            <text textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={player.label.length > 3 ? Math.max(9, (compact ? 48 : 60) / player.label.length) : compact ? 20 : 24} fontWeight="800">{player.label}</text>
            {board.ballHandlerId === player.id && (
              <g data-ball="true" transform={compact ? `translate(${20/marker.x} ${-14/marker.y}) scale(.65)` : 'translate(29 -24)'} aria-label="Ball handler" className={animation?.dribbling.includes(player.id) ? 'animation-dribble' : undefined}>
                {tool === 'animate' && <circle r={compact ? marker.hit/.65 : 23} fill="transparent" clipPath={compact ? `url(#${uid}-ball-hit)` : undefined} />}
                <circle r="13" fill={themeColors.blaze} stroke="#fff" strokeWidth="3" />
                <path d="M-12 0h24M0-12v24M-9-8c8 5 10 11 9 20M9-8C1-3-1 4 0 12" fill="none" stroke="#552916" strokeWidth="1.7" />
              </g>
            )}
          </g>
        )
      })}
      {catchMarkers.map(marker => {
        const p = displayPoint(dragCatch?.id === marker.id ? dragCatch.point : marker.point, board.flipped)
        return <g key={marker.id} data-catch-id={marker.id} transform={`translate(${p.x*WIDTH} ${p.y*HEIGHT})`}><circle className="catch-handle" r="40" fill="none" stroke="transparent" strokeWidth="24" pointerEvents="stroke" /><circle r="40" fill="none" stroke={themeColors.blazeHover} strokeWidth="10" strokeDasharray="9 5" pointerEvents="none" /><title>Drag catch ring along receiver path</title></g>
      })}
      {board.looseBall && (() => { const p = displayPoint(board.looseBall, board.flipped); return <g data-ball="true" aria-label="Ball in flight" transform={`translate(${p.x*WIDTH} ${p.y*HEIGHT})${compact ? ` scale(${marker.x} ${marker.y})` : ''}`}>{tool === 'animate' && <circle r={compact ? marker.hit : 24} fill="transparent" />}<circle r={compact ? 8.45 : 16} fill={themeColors.blaze} stroke="white" strokeWidth="3" /></g> })()}
    </svg>
    </>
  )
}
